import { React, moment } from 'Imports';
import { eventStatusIndicatorColors } from '$CSS/eventStatusIndicatorColors';
import { Card, Divider } from 'MaterialUIComponents';
import { IConfigServiceInjectedProps, ConfigService } from '$State/ConfigFreezerService';
import { IIntegrationPartnerDataInjectedProps, IntegrationPartnerDataService } from '$State/IntegrationPartnerDataFreezerService';
import { IVideoEventServiceInjectedProps, VideoEventService } from '$State/VideoEventFreezerService';
import { RolesEnum, canEditByRole } from 'externals/VerifyRole';
import { VideoEventCardThumbnail } from '$Components/VideoEvents/VideoEventCardThumbnail/VideoEventCardThumbnail';
import { getStatusLabel } from '$Utilities/videoEventStatusUtility';
import { VideoEventDescription } from '$Components/Shared/VideoEventDescription';
import VideoEventInfo from '$Components/VideoEvents/VideoEventInfo/VideoEventInfo';
import {
    VideoEventResponse,
    VideoEventWorkflowStatusIdEnum,
    UserResponse,
    VideoEventScoreResponse,
    BulkRatingRequest,
} from '$Generated/api';
import * as VideoAssets from '$Components/Shared/VideoAssetsUrls';
import * as _ from 'lodash';
import { EllipsisItem, EllipsisPopperMenu } from '$Components/Shared/EllipsisPopperMenu';
import { isEncompass } from '$Utilities/integrationPlatform';
import { useEffect, useRef, useState } from 'react';
import { useAppSnackbar } from '$Utilities/hooks/useAppSnackbar';
import { VideoEventListBulkScoreModal } from '../VideoEventList/VideoEventListBulkScoreModal';
import { AlertTypes } from '$Utilities/Enums/AlertTypes';
import VideoEventFlagButton from '../VideoEventFlagButton/VideoEventFlagButton';

export const styles = require('$Components/VideoEvents/VideoEventCard/VideoEventCard.scss') as {
    main: string;
    indicator: string;
    card: string;
    thumbnail: string;
    cardBody: string;
    event: string;
    eventStatus: string;
    eventStatusDescriptionGeotab: string;
    eventStatusDescriptionEncompass: string;
    flexRow: string;
    boxEncompass: string;
    eventInfo: string;
    description: string;
};

interface IVideoEventCardProps {
    event: VideoEventResponse;
    onSelectVideoEvent: (eventId: string, goBack: boolean) => void;
    onFlagVideoEvent?: (eventId: number | undefined) => void;
    handleVideoPlayed?: (eventid: number | undefined) => void;
    videoEventStatus?: VideoEventWorkflowStatusIdEnum | undefined;
    toggleDeletePopup: (selectedId?: number) => void;
    deleteButtonDisabled: boolean;
    toggleDismissPopup: (selectedId?: number) => void;
}

const _VideoEventCard = (
    props: IVideoEventCardProps & IConfigServiceInjectedProps & IIntegrationPartnerDataInjectedProps & IVideoEventServiceInjectedProps,
) => {
    const { showSnackbarMessage } = useAppSnackbar();
    const cardElementRef = useRef<HTMLDivElement | null>(null);
    const [videoEventScores, setVideoEventScores] = useState<VideoEventScoreResponse[]>([]);
    const [flaggedByUsers, setFlaggedByUsers] = useState<UserResponse[]>(props.event.flaggedByUsers ?? ([] as UserResponse[]));
    const [openScorePopup, setOpenScorePopup] = useState<boolean>(false);
    const [videoEvent, setVideoEvent] = useState<VideoEventResponse>(props.event);

    // Without this, videoEvent would stay pinned to whatever props.event was on mount -- e.g. a play
    // updating the event's status to In_Progress mutates the freezer's list item (a new object reference
    // reaching this card as a fresh props.event), but this component's own key doesn't change, so React
    // never remounts it and the label would keep reading the stale initial copy until a full reload.
    useEffect(() => {
        setVideoEvent(props.event);
    }, [props.event]);

    useEffect(() => {
        if (videoEventScores.length === 0) {
            const ratings = props.videoEvents.getState().videoEventRatings.data ?? [];
            if (ratings.length > 0) {
                setVideoEventScores(ratings);
            }
        }
    }, []);

    const getIndicatorColor = React.useCallback((): { background: string; color: string } => {
        if (videoEvent.videoEventStatus != undefined) {
            return eventStatusIndicatorColors[videoEvent.videoEventStatus];
        } else {
            return { background: '', color: '' };
        }
    }, [videoEvent]);

    const eventIsFlaggedByCurrentUser = React.useCallback((): boolean => {
        const currentId = IntegrationPartnerDataService.getState().currentUserResult.data.id;
        return (
            _.find(flaggedByUsers, (user: UserResponse) => {
                return user.integrationProviderSourceId === currentId;
            }) !== undefined
        );
    }, [flaggedByUsers]);

    const onFlagVideo = React.useCallback(
        (eventId: number | undefined) => {
            if (!canEditByRole([RolesEnum.videoEventEdit, RolesEnum.encompassVideo])) return;
            if (props.onFlagVideoEvent) {
                const currentUser = IntegrationPartnerDataService.getState().currentUserResult.data;
                if (eventIsFlaggedByCurrentUser()) {
                    setFlaggedByUsers((prev) => _.filter(prev, (user: UserResponse) => user.email !== currentUser.name));
                } else {
                    setFlaggedByUsers((prev) =>
                        prev.concat([
                            {
                                id: '',
                                email: currentUser.name,
                                integrationProviderSourceId: currentUser.id,
                                displayName: `${currentUser.firstName} ${currentUser.lastName}`,
                            },
                        ]),
                    );
                }

                props.onFlagVideoEvent(eventId);
            }
        },
        [props, eventIsFlaggedByCurrentUser],
    );

    const handleVideoPlayed = React.useCallback(
        (eventId: number | undefined) => {
            // make sure the event status is not completed or dismissed
            if (videoEvent.videoEventStatus !== undefined && videoEvent.videoEventStatus == 'New') {
                if (props.handleVideoPlayed) {
                    props.handleVideoPlayed(eventId);
                }
            }
        },
        [props],
    );

    const eventDate = props.event.eventStartDate ? moment(props.event.eventStartDate) : moment();
    const assets = props.event.videoAsset || [];

    const {
        PreviewUrl: vidUrl,
        ThumbnailUrl: thumbnailURL,
        DownloadUrl: downloadUrl,
    } = VideoAssets.findPreviewUrls(assets, props.config.getBaseUrl());

    const eventId = props.event.id ? props.event.id : 0;
    const displayEventId = props.event.displayId ? props.event.displayId : '';

    const stepStatus = videoEvent.workflowStep;
    const stepDescription = videoEvent.workflowStepDescription;
    const indicatorColor = getIndicatorColor();
    const indicatorText = getStatusLabel(videoEvent.videoEventStatus as VideoEventWorkflowStatusIdEnum);

    const canEdit = canEditByRole([RolesEnum.videoEventEdit, RolesEnum.encompassVideo]);

    const ellipsisMenuitems: EllipsisItem[] = [
        {
            key: 'score',
            text: 'Score',
            isHidden: videoEvent.videoEventStatus === 'Dismissed' || videoEvent.videoEventStatus === 'Completed' || !canEdit,
            onClick: () => setOpenScorePopup(true),
        },
        {
            key: 'dismiss',
            text: 'Dismiss',
            isHidden: videoEvent.videoEventStatus === 'Dismissed' || videoEvent.videoEventStatus === 'Completed' || !canEdit,
            onClick: () => props.toggleDismissPopup(eventId),
        },
        {
            key: 'delete',
            text: 'Delete',
            isHidden: !canEditByRole([RolesEnum.videoAdmin]),
            onClick: () => props.toggleDeletePopup(eventId),
            isDisabled: props.deleteButtonDisabled || !canEditByRole([RolesEnum.videoAdmin]),
        },
    ];

    // Return the selected event ids as a Promise<number[]>
    const getSelectedEventsFunc = async (): Promise<number[]> => {
        return [props.event.id ?? 0];
    };

    const handleSubmitScore = async (body: BulkRatingRequest): Promise<boolean> => {
        const successResponse = await props.videoEvents.postVideoEventRatings(body);
        if (stepDescription == 'New') {
            await props.videoEvents.updateLocalVideoEventStatus('In_Progress', props.event.id);
        }

        if (successResponse) {
            showSnackbarMessage({
                message: `Video event scored successfully.`,
                alertType: AlertTypes.Success,
                isEncompass: isEncompass,
                showIcon: isEncompass,
            });
            setOpenScorePopup(false);
        } else {
            showSnackbarMessage({
                message: `Video event unable to be scored.`,
                alertType: AlertTypes.Fail,
                isEncompass: isEncompass,
                showIcon: isEncompass,
            });
        }

        //get the updated video event to refresh event card instead of the entire list from video events page.
        await props.videoEvents.getVideoEvent(displayEventId);
        const selectedVideoEventResult = props.videoEvents.getState().selectedVideoEventResult.data;
        if (selectedVideoEventResult) {
            setVideoEvent(selectedVideoEventResult);
        }
        return true;
    };

    return (
        <div className={styles.main} ref={cardElementRef}>
            {indicatorColor.background !== '' && (
                <div className={styles.indicator} style={{ backgroundColor: indicatorColor.background, color: indicatorColor.color }}>
                    {indicatorText}
                </div>
            )}
            <Card className={styles.card} style={isEncompass ? { boxShadow: '#B5BEC9 0 4px' } : {}} elevation={0}>
                {(stepStatus !== 'Success' || !thumbnailURL) && (
                    <div className={styles.thumbnail}>
                        <div className={styles.eventStatus} onClick={() => props.onSelectVideoEvent(displayEventId, false)}>
                            {stepStatus == 'Success' ? 'No Video' : stepStatus}
                            <div className={isEncompass ? styles.eventStatusDescriptionEncompass : styles.eventStatusDescriptionGeotab}>
                                {stepDescription}
                            </div>
                        </div>
                    </div>
                )}

                {thumbnailURL && stepStatus === 'Success' && (
                    <div style={{ height: '150px', backgroundColor: 'black' }}>
                        <VideoEventCardThumbnail
                            eventId={eventId}
                            displayEventId={displayEventId}
                            thumbnailURL={thumbnailURL}
                            videoURL={vidUrl}
                            onSelectVideoEvent={props.onSelectVideoEvent}
                            handleVideoPlayed={handleVideoPlayed}
                        />
                    </div>
                )}

                <div className={styles.cardBody} onClick={() => props.onSelectVideoEvent(displayEventId, false)} title="View Details">
                    <div className={styles.event}>
                        <div className={styles.description}>
                            <VideoEventDescription
                                videoEventTypePairings={videoEvent.videoEventTypePairings}
                                videoEventTypeString={videoEvent.videoEventTypeString}
                                isVideoEventDetails={false}
                                videoEventFontSize={isEncompass ? '16px' : '18px'}
                                videoEventFontWeight={isEncompass ? '700' : '500'}
                            />
                        </div>
                    </div>
                    <VideoEventInfo
                        event={videoEvent}
                        eventDate={eventDate}
                        className={styles.eventInfo}
                        videoEventRatings={videoEventScores}
                    ></VideoEventInfo>

                    <Divider sx={{ paddingTop: '4px', marginBottom: '4px' }} />

                    <div className={styles.flexRow}>
                        <VideoEventFlagButton
                            videoEventId={videoEvent.id ?? 0}
                            flaggedByUsers={flaggedByUsers}
                            onClick={onFlagVideo}
                            showLabel
                            userCanEdit={canEdit}
                            labelSize="12px"
                            iconSize="14px"
                        />
                        {ellipsisMenuitems.findIndex((item) => !item.isHidden) !== -1 && (
                            <EllipsisPopperMenu items={ellipsisMenuitems} isEncompass={isEncompass} />
                        )}
                    </div>
                </div>
            </Card>
            <VideoEventListBulkScoreModal
                isOpen={openScorePopup}
                handleModalClosed={() => setOpenScorePopup(false)}
                selectedEventsCount={1}
                videoEventRatings={videoEventScores ?? []}
                handleSubmit={handleSubmitScore}
                getSelectedEvents={getSelectedEventsFunc}
                isSingleScore
                selectedEvent={videoEvent}
                skipSubmitMessage
            />
        </div>
    );
};

export const VideoEventCard = VideoEventService.inject(ConfigService.inject(IntegrationPartnerDataService.inject(_VideoEventCard)));
